import React, { useState, useEffect, useCallback } from 'react';
import { useLocale } from '@platform/core';
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../contexts/firebase';
import { usePlanning } from '../contexts/PlanningContext';

interface Widget {
  name: string;
  size: number;
  ordinal: number;
  povName?: string;
  chartType?: string;
}

interface Dashboard {
  id: string;
  Name: string;
  widgets: Widget[];
}

const SIZES = [12, 6, 4, 3, 2];

export default function DashboardsPage() {
  const { t } = useLocale();
  const { currentCompany } = usePlanning();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addWidgetMode, setAddWidgetMode] = useState(false);
  const [widgetListMode, setWidgetListMode] = useState(false);
  const [newWidgetName, setNewWidgetName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addDashboardMode, setAddDashboardMode] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');

  const fsGuid = currentCompany
    ? `companies/${currentCompany.id}/Dashboards`
    : null;

  useEffect(() => {
    if (!fsGuid) {
      setDashboards([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const colRef = collection(db, fsGuid);
    const unsub = onSnapshot(colRef, (snap) => {
      const data = snap.docs.map((d) => ({
        id: d.id,
        Name: 'Dashboard',
        widgets: [],
        ...d.data(),
      })) as Dashboard[];
      setDashboards(data);
      setLoading(false);
    });
    return () => unsub();
  }, [fsGuid]);

  const updateDashboard = useCallback(
    async (id: string, updates: Partial<Dashboard>) => {
      if (!fsGuid) return;
      await setDoc(doc(db, fsGuid, id), updates, { merge: true });
    },
    [fsGuid]
  );

  const createDashboard = async () => {
    if (!fsGuid || !newDashboardName.trim()) return;
    await addDoc(collection(db, fsGuid), {
      Name: newDashboardName,
      widgets: [],
      CreatedDate: serverTimestamp(),
    });
    setNewDashboardName('');
    setAddDashboardMode(false);
  };

  const removeDashboard = async (id: string) => {
    if (!fsGuid) return;
    await deleteDoc(doc(db, fsGuid, id));
    setConfirmDelete(false);
    setActiveTab(0);
  };

  const addWidget = (dashboard: Dashboard) => {
    if (!newWidgetName.trim()) return;
    const widgets = [
      ...dashboard.widgets,
      {
        name: newWidgetName,
        size: 4,
        ordinal: dashboard.widgets.length,
      },
    ];
    updateDashboard(dashboard.id, { widgets });
    setNewWidgetName('');
    setAddWidgetMode(false);
  };

  const removeWidget = (dashboard: Dashboard, idx: number) => {
    const widgets = dashboard.widgets.filter((_, i) => i !== idx);
    updateDashboard(dashboard.id, { widgets: reorder(widgets) });
  };

  const moveWidget = (dashboard: Dashboard, idx: number, direction: -1 | 1) => {
    const widgets = [...dashboard.widgets];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= widgets.length) return;
    [widgets[idx], widgets[newIdx]] = [widgets[newIdx], widgets[idx]];
    updateDashboard(dashboard.id, { widgets: reorder(widgets) });
  };

  const resizeWidget = (dashboard: Dashboard, idx: number, size: number) => {
    const widgets = [...dashboard.widgets];
    widgets[idx] = { ...widgets[idx], size };
    updateDashboard(dashboard.id, { widgets });
  };

  const reorder = (widgets: Widget[]): Widget[] =>
    widgets.map((w, i) => ({ ...w, ordinal: i }));

  if (loading) {
    return (
      <div className="db-loading">
        <div className="loading-spinner" />
        <p>{t('general.loading') || 'Loading...'}</p>
      </div>
    );
  }

  const activeDashboard = dashboards[activeTab];

  return (
    <div className="db-page">
      <div className="page-header">
        <h1>📊 {t('planning.dashboards') || 'Dashboards'}</h1>
        {addDashboardMode ? (
          <div className="db-inline-add">
            <input
              className="mg-cell-input"
              placeholder={t('common.name') || 'Name'}
              value={newDashboardName}
              onChange={(e) => setNewDashboardName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') createDashboard();
                if (e.key === 'Escape') setAddDashboardMode(false);
              }}
              autoFocus
            />
            <button className="mg-btn mg-btn-primary" onClick={createDashboard}>
              {t('common.add') || 'Add'}
            </button>
            <button className="mg-btn mg-btn-outline" onClick={() => setAddDashboardMode(false)}>
              ✕
            </button>
          </div>
        ) : (
          <button className="mg-btn mg-btn-primary" onClick={() => setAddDashboardMode(true)}>
            ➕ {t('common.add') || 'Add Dashboard'}
          </button>
        )}
      </div>

      {dashboards.length > 0 && (
        <>
          <div className="db-tabs">
            {dashboards.map((d, i) => (
              <button
                key={d.id}
                className={`db-tab ${i === activeTab ? 'active' : ''}`}
                onClick={() => { setActiveTab(i); setConfirmDelete(false); }}
              >
                {d.Name}
              </button>
            ))}
          </div>

          {activeDashboard && (
            <div className="db-content">
              <div className="db-toolbar">
                <button
                  className={`mg-btn mg-btn-sm ${addWidgetMode ? 'mg-btn-primary' : 'mg-btn-outline'}`}
                  onClick={() => setAddWidgetMode(!addWidgetMode)}
                >
                  ➕
                </button>
                <button
                  className={`mg-btn mg-btn-sm ${widgetListMode ? 'mg-btn-primary' : 'mg-btn-outline'}`}
                  onClick={() => setWidgetListMode(!widgetListMode)}
                >
                  📋
                </button>
                {!confirmDelete ? (
                  <button
                    className="mg-btn mg-btn-sm mg-btn-outline"
                    onClick={() => setConfirmDelete(true)}
                  >
                    🗑️
                  </button>
                ) : (
                  <>
                    <button
                      className="mg-btn mg-btn-sm"
                      style={{ background: 'var(--error-color, #d32f2f)', color: 'white', borderColor: 'var(--error-color, #d32f2f)' }}
                      onClick={() => removeDashboard(activeDashboard.id)}
                    >
                      {t('common.delete') || 'Delete'}
                    </button>
                    <button
                      className="mg-btn mg-btn-sm mg-btn-outline"
                      onClick={() => setConfirmDelete(false)}
                    >
                      {t('common.cancel') || 'Cancel'}
                    </button>
                  </>
                )}
              </div>

              {/* Widget list editor */}
              {widgetListMode && (
                <div className="db-widget-list">
                  <table className="db-widget-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('common.name') || 'Name'}</th>
                        <th>{t('common.size') || 'Size'}</th>
                        <th>{t('common.actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeDashboard.widgets.map((w, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{w.name}</td>
                          <td>
                            <select
                              className="db-size-select"
                              value={w.size}
                              onChange={(e) => resizeWidget(activeDashboard, i, Number(e.target.value))}
                            >
                              {SIZES.map((s) => (
                                <option key={s} value={s}>{12 / s} per row</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {i > 0 && (
                              <button className="mg-btn mg-btn-sm mg-btn-outline" onClick={() => moveWidget(activeDashboard, i, -1)}>↑</button>
                            )}
                            {i < activeDashboard.widgets.length - 1 && (
                              <button className="mg-btn mg-btn-sm mg-btn-outline" onClick={() => moveWidget(activeDashboard, i, 1)}>↓</button>
                            )}
                            <button
                              className="mg-btn mg-btn-sm mg-btn-outline"
                              style={{ color: 'var(--error-color, #d32f2f)' }}
                              onClick={() => removeWidget(activeDashboard, i)}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add widget */}
              {addWidgetMode && (
                <div className="db-add-widget">
                  <input
                    className="mg-cell-input"
                    placeholder={t('planning.widgetName') || 'Widget name'}
                    value={newWidgetName}
                    onChange={(e) => setNewWidgetName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addWidget(activeDashboard);
                      if (e.key === 'Escape') setAddWidgetMode(false);
                    }}
                    autoFocus
                  />
                  <button className="mg-btn mg-btn-sm mg-btn-primary" onClick={() => addWidget(activeDashboard)}>
                    {t('common.add') || 'Add'}
                  </button>
                </div>
              )}

              {/* Widget grid */}
              <div className="db-widgets-grid">
                {activeDashboard.widgets.length === 0 ? (
                  <div className="db-empty">
                    <p>{t('planning.noWidgets') || 'No widgets yet. Click ➕ to add one.'}</p>
                  </div>
                ) : (
                  activeDashboard.widgets.map((w, i) => (
                    <div
                      key={i}
                      className="db-widget-card"
                      style={{ gridColumn: `span ${12 / w.size}` }}
                    >
                      <div className="db-widget-header">
                        <span>{w.name}</span>
                      </div>
                      <div className="db-widget-body">
                        <p className="db-widget-placeholder">
                          {t('planning.widgetPlaceholder') || 'Widget content — connect to a data source'}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}

      {dashboards.length === 0 && (
        <div className="db-empty-state">
          <p>📊 {t('planning.noDashboards') || 'No dashboards yet.'}</p>
        </div>
      )}

      <style>{`
        .db-page { max-width: 1200px; margin: 0 auto; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
        .page-header h1 { margin: 0; font-size: 1.5rem; }

        .db-tabs { display: flex; gap: 0; border-bottom: 2px solid var(--border-color, #e0e0e0); margin-bottom: 1rem; }
        .db-tab { padding: 0.5rem 1rem; border: none; background: transparent; cursor: pointer; font-size: 0.875rem; border-bottom: 2px solid transparent; margin-bottom: -2px; }
        .db-tab:hover { background: var(--background-color, #f5f5f5); }
        .db-tab.active { border-bottom-color: var(--primary-color, #1976d2); color: var(--primary-color, #1976d2); font-weight: 600; }

        .db-content { background: var(--surface-color, #fff); border-radius: var(--radius-lg, 12px); border: 1px solid var(--border-color, #e0e0e0); padding: 1rem; }

        .db-toolbar { display: flex; gap: 0.5rem; margin-bottom: 1rem; }

        .db-widget-list { margin-bottom: 1rem; overflow-x: auto; }
        .db-widget-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .db-widget-table th, .db-widget-table td { padding: 0.5rem; text-align: start; border-bottom: 1px solid var(--border-color, #e0e0e0); }
        .db-widget-table thead { background: var(--background-color, #f5f5f5); }
        .db-size-select { padding: 0.25rem; border: 1px solid var(--border-color, #e0e0e0); border-radius: 4px; font-size: 0.75rem; }

        .db-add-widget { display: flex; gap: 0.5rem; align-items: center; margin-bottom: 1rem; }

        .db-widgets-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 1rem; }

        .db-widget-card { background: var(--background-color, #f5f5f5); border-radius: var(--radius-md, 8px); border: 1px solid var(--border-color, #e0e0e0); overflow: hidden; min-height: 150px; }
        .db-widget-header { padding: 0.5rem 0.75rem; font-weight: 600; font-size: 0.8125rem; background: var(--surface-color, #fff); border-bottom: 1px solid var(--border-color, #e0e0e0); }
        .db-widget-body { padding: 1rem; display: flex; align-items: center; justify-content: center; }
        .db-widget-placeholder { color: var(--text-secondary, #757575); font-size: 0.8125rem; text-align: center; }

        .db-empty, .db-empty-state { text-align: center; padding: 3rem; color: var(--text-secondary, #757575); }
        .db-loading { display: flex; flex-direction: column; align-items: center; padding: 3rem; gap: 1rem; }
        .db-inline-add { display: flex; gap: 0.5rem; align-items: center; }

        .mg-btn { padding: 0.375rem 0.75rem; border-radius: var(--radius-md, 8px); font-size: 0.8125rem; cursor: pointer; border: 1px solid var(--border-color, #e0e0e0); background: var(--surface-color, #fff); transition: all 0.15s; }
        .mg-btn:hover { background: var(--background-color, #f5f5f5); }
        .mg-btn-primary { background: var(--primary-color, #1976d2); color: white; border-color: var(--primary-color, #1976d2); }
        .mg-btn-outline { background: transparent; }
        .mg-btn-sm { padding: 0.25rem 0.5rem; font-size: 0.75rem; }
        .mg-cell-input { padding: 0.375rem 0.5rem; border: 1px solid var(--border-color, #e0e0e0); border-radius: 4px; font-size: 0.875rem; }

        @media (max-width: 768px) {
          .db-page { max-width: 100%; }
          .page-header { flex-direction: column; gap: 0.5rem; align-items: stretch; }
          .db-tabs { overflow-x: auto; -webkit-overflow-scrolling: touch; flex-wrap: nowrap; }
          .db-tab { min-width: max-content; min-height: 44px; }
          .db-content { padding: 0.75rem; }
          .db-toolbar { flex-wrap: wrap; }
          .db-widgets-grid { grid-template-columns: 1fr !important; }
          .db-widget-card { grid-column: span 1 !important; }
          .db-widget-list { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .db-add-widget { flex-direction: column; align-items: stretch; }
          .db-inline-add { flex-direction: column; }
          .mg-btn { min-height: 44px; }
          .mg-cell-input { min-height: 44px; }
        }
      `}</style>
    </div>
  );
}
